/**
 * Connectivity monitor — single source of truth for "is the device
 * online?" plus a hook for things that need to react when the answer
 * changes. See design §3.9 / §5.6 and plan issue 4.2.
 *
 * Two layers of detection:
 *   1. `navigator.onLine` events (`online` / `offline`) — instantaneous,
 *      but unreliable on some platforms (Chrome on Linux famously
 *      reports false positives over Wi-Fi networks with no internet).
 *   2. Frontend-origin polling every 7 seconds — slower, but verifies
 *      internet reachability without relying on a third-party service that
 *      a corporate network may block. A backend outage deliberately does
 *      NOT count as offline; API failures keep their existing error/restart
 *      handling instead of enabling offline mode.
 *
 * The provider lives at App level (next to <IsOfflineBar />), NOT inside
 * <ConfigProvider>, because the existing offline banner is mounted
 * outside the config tree and uses the same data. Phase 5+ offline UI
 * inside the config tree can read from the same provider via
 * `useConnectivity()`.
 *
 * The provider does NOT itself check the `enable_offline_checks` flag.
 * Connectivity is interesting to non-offline tenants too (the existing
 * banner has been telling everyone "your internet connection is
 * disrupted" for years). What IS flag-gated is:
 *   - The pending-sessions count (a tenant without the offline feature
 *     never has pending sessions; we skip the IndexedDB read).
 *   - The replay-engine trigger via `onOfflineToOnlineTransition` —
 *     subscribers gate themselves on the flag (see Phase 6).
 */

import axios from "axios";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import { getOfflineDB, STORE_SESSIONS } from "./db";
import { getOfflineFeatureEnabled } from "./featureFlag";

const CONNECTIVITY_POLL_INTERVAL_MS = 7000;
const CONNECTIVITY_POLL_TIMEOUT_MS = 7000;
const PENDING_REFRESH_INTERVAL_MS = 5000;

export interface ConnectivityContextValue {
    /** True when the device appears to be online (navigator + frontend-origin check). */
    isOnline: boolean;
    /** Count of OfflineCheckSession rows in `ready_to_sync` status. */
    pendingSessions: number;
}

const Context = createContext<ConnectivityContextValue>({
    isOnline: true,
    pendingSessions: 0,
});

type TransitionCallback = () => void;
const transitionCallbacks: Set<TransitionCallback> = new Set();

/**
 * Register a callback that fires every time the device transitions
 * from offline to online. Returns an unsubscribe function. The
 * callback is invoked outside React (no hooks); subscribers must gate
 * themselves on the master flag if they only want to act for opted-in
 * tenants.
 *
 * Used by the Phase 6 replay engine to kick off pending sessions when
 * connectivity returns. Exposed as a free function (not a hook) so a
 * non-React module can subscribe.
 */
export function onOfflineToOnlineTransition(
    cb: TransitionCallback,
): () => void {
    transitionCallbacks.add(cb);
    return () => {
        transitionCallbacks.delete(cb);
    };
}

async function probeFrontend(): Promise<boolean> {
    try {
        await axios.get(`${window.location.origin}/favicon.ico`, {
            // Workbox precaches static assets. Its default matching ignores only
            // utm_* and fbclid params, so this unique param forces a network fetch.
            params: { connectivity_probe: Date.now() },
            headers: { "Cache-Control": "no-cache" },
            timeout: CONNECTIVITY_POLL_TIMEOUT_MS,
        });
        return true;
    } catch {
        return false;
    }
}

async function readPendingSessionCount(): Promise<number> {
    if (!getOfflineFeatureEnabled()) return 0;
    try {
        const db = await getOfflineDB();
        const index = db.transaction(STORE_SESSIONS).store.index("by-status");
        return await index.count(IDBKeyRange.only("ready_to_sync"));
    } catch {
        return 0;
    }
}

export function ConnectivityProvider({
    children,
}: {
    children: React.ReactNode;
}): JSX.Element {
    const [isOnline, setIsOnline] = useState<boolean>(window.navigator.onLine);
    const [pendingSessions, setPendingSessions] = useState<number>(0);
    const wasOnline = useRef<boolean>(window.navigator.onLine);

    // navigator.onLine events.
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    // Frontend-origin polling — supplements navigator.onLine to catch its lies in
    // BOTH directions:
    //   1. Stuck-online (the case that motivated this logic):
    //      navigator.onLine reports true even though the network is dead
    //      (Chrome on Linux over Wi-Fi with no internet, but also
    //      observed transiently during SPA navigation while DevTools
    //      throttling is set to "Offline" — see EFQ-2962 local
    //      validation). An unreachable frontend origin demotes us back to offline.
    //   2. Stuck-offline: navigator.onLine reports false but the network
    //      actually came back. A reachable frontend origin promotes us to online.
    // navigator.onLine=false is still authoritative offline (no point
    // probing if the OS already says no link).
    useEffect(() => {
        const tick = async () => {
            if (!window.navigator.onLine) {
                setIsOnline(false);
                return;
            }
            const reachable = await probeFrontend();
            setIsOnline(reachable);
        };
        tick();
        const id = setInterval(tick, CONNECTIVITY_POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, []);

    // Fire transition callbacks on offline→online edges.
    useEffect(() => {
        if (isOnline && !wasOnline.current) {
            transitionCallbacks.forEach((cb) => {
                try {
                    cb();
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error("[offline] transition callback threw", err);
                }
            });
        }
        wasOnline.current = isOnline;
    }, [isOnline]);

    // Refresh the pending-session count on a slow timer plus on
    // online-state changes (so the banner updates when sync completes).
    useEffect(() => {
        let cancelled = false;
        const refreshCount = async () => {
            const n = await readPendingSessionCount();
            if (!cancelled) setPendingSessions(n);
        };
        refreshCount();
        const id = setInterval(refreshCount, PENDING_REFRESH_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [isOnline]);

    return (
        <Context.Provider value={{ isOnline, pendingSessions }}>
            {children}
        </Context.Provider>
    );
}

export function useConnectivity(): ConnectivityContextValue {
    return useContext(Context);
}

/** For tests only. Drop all subscribers. */
export function _resetTransitionCallbacksForTests(): void {
    transitionCallbacks.clear();
}
